import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useSelector } from 'react-redux';
import { getEventDetails } from '@store/slices/eventSlice';
import {
  APP_HOST,
  usePublicCoLocatedEventsByEventId,
} from '../../../../utils/apiHooks';
import { useCallback, useMemo } from 'react';
import {
  MenuItem as MenuItemType,
  PublicEventDetailsResponse,
} from '@hubs101/booking-api-skd-client/lib/types';
import {
  ButtonsWrapper,
  DesktopMenuWrapper,
  LogoImage,
  LogoWrapper,
} from './styles';
import { getImageSrc } from '../../../../utils/fn';
import weLogo from '@assets/images/weCONECT_col_RGB.svg';
import Button from '@components/Button';
import { useNavigate } from 'react-router-dom';
import { EventPosterSectionMenuItems } from '../EventPosterSectionMenuItems';
import { useExtraLargeDesktopWidth } from '@utils/hooks';

export const mappingLinks = {
  home: '',
  agenda: 'agenda-url',
  speaker: 'speakers-url',
  partner: 'partners-url',
  'who-why': 'who-&-why-url',
  faq: 'faq-url',
  'content-hub': 'content-hub-url',
};

export const getDynamicText = (
  text: string,
  sluggedEvent: PublicEventDetailsResponse | undefined,
  coLocatedEvents: PublicEventDetailsResponse[] | undefined
) => {
  let updatedText = `${text}`;

  updatedText = updatedText.replaceAll('{eventName}', sluggedEvent?.name ?? '');
  updatedText = updatedText.replaceAll(
    '{colEventNr}',
    `${coLocatedEvents?.length ?? 0}`
  );
  updatedText = updatedText.replaceAll(
    '{eventLocationCity}',
    `${sluggedEvent?.location?.marker?.city ?? ''}`
  );

  return updatedText;
};

export const getMailToLink = ({
  sluggedEvent,
  coLocatedEvents,
  email,
  subject = '',
  body = '',
}: {
  sluggedEvent?: PublicEventDetailsResponse;
  coLocatedEvents: PublicEventDetailsResponse[] | undefined;
  email: string;
  subject: string;
  body: string;
}) => {
  const params = new URLSearchParams({
    subject: getDynamicText(subject, sluggedEvent, coLocatedEvents),
    body: getDynamicText(body, sluggedEvent, coLocatedEvents),
  });
  const mailto = `mailto:${email}?${params.toString()}`;

  return mailto;
};

export const EventPosterSectionMenu = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const navigate = useNavigate();
  const smallerThanExtraLargeDesktop = useExtraLargeDesktopWidth();

  const { data: coLocatedEvents, isLoading: isCoLocatedEventsLoading } =
    usePublicCoLocatedEventsByEventId({
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
    });

  const sortedMenuItems = useMemo(
    () =>
      sluggedEvent?.website_menu
        ?.find(e => e.type === 'mainmenu')
        ?.items?.toSorted(
          (a: MenuItemType, b: MenuItemType) => a.order - b.order
        ),
    [sluggedEvent?.website_menu]
  );

  const link = useCallback(
    (item: MenuItemType) => {
      switch (item.type) {
        case 'internal':
          return `/${String(item?.url ?? '')}`;
        case 'external':
          return item?.url ?? '';
        case 'mailto':
          return getMailToLink({
            sluggedEvent: sluggedEvent,
            coLocatedEvents: coLocatedEvents,
            email: item?.to ?? '',
            subject: item?.subject ?? '',
            body: item?.body ?? '',
          });
        default:
          return t(
            mappingLinks[item.type as keyof typeof mappingLinks] ??
              `/${String(item.url)}`
          );
      }
    },
    [t]
  );

  const mainMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => !e.level)
        ?.map((item: MenuItemType) => {
          return {
            id: item.label,
            title: item.label,
            subtitle: item.subtitle,
            link: link(item),
            order: item.order,
            to: item?.to ?? '',
            subject: item?.subject ?? '',
            body: item?.body ?? '',
            styling: String(item.styling),
            element: item?.element,
          };
        }),
    [link, sortedMenuItems]
  );

  const secondaryMenuItems = useMemo(
    () =>
      sortedMenuItems
        ?.filter(e => e.level)
        ?.map((item: MenuItemType) => {
          return {
            id: item.label,
            title: item.label,
            subtitle: item.subtitle,
            link: link(item),
            order: item.order,
            index: mainMenuItems?.findLastIndex(
              element => element.order < item.order
            ),
            isDesktopDisabled: item.isDesktopDisabled,
            isTabletDisabled: item.isTabletDisabled,
            isMobileDisabled: item.isMobileDisabled,
            to: item?.to ?? '',
            subject: item?.subject ?? '',
            body: item?.body ?? '',
            styling: String(item?.styling),
            type: item?.type,
            element: item?.element,
          };
        }),
    [link, mainMenuItems, sortedMenuItems]
  );

  return (
    <DesktopMenuWrapper>
      <LogoWrapper>
        <LogoImage
          $maxWidth={'15.6rem'}
          src={
            sluggedEvent?.design?.eventLogoNegSvg?.length > 0
              ? getImageSrc(sluggedEvent?.design?.eventLogoNegSvg)
              : weLogo
          }
          alt={
            sluggedEvent?.name?.length ? sluggedEvent?.name : 'WeConect Logo'
          }
          $height={`${4}rem`}
          onClick={() => {
            window.location.href = `${APP_HOST}/events/${sluggedEvent?.slug}`;
          }}
        />
      </LogoWrapper>

      <EventPosterSectionMenuItems
        mainMenuItems={mainMenuItems}
        sluggedEvent={sluggedEvent}
        secondaryMenuItems={secondaryMenuItems}
        themeColor={sluggedEvent?.design?.themeColor}
        coLocatedEvents={coLocatedEvents}
      />

      <ButtonsWrapper>
        <Button
          title={t('exhibit-now')}
          backgroundColor={theme.color.transparent}
          hoverBackgroundColor={theme.color.white}
          textColor={theme.color.white}
          hoverTextColor={sluggedEvent?.design?.themeColor}
          border={`1px solid ${theme.color.white}`}
          borderRadius={theme.borderRadius.radius10}
          fontSize={
            smallerThanExtraLargeDesktop
              ? theme.fontSize.size15
              : theme.fontSize.size20
          }
          lineHeight={theme.lineHeight.height100}
          fontWeight="normal"
          padding="0.6rem 3rem"
          width="auto"
          margin="0 1rem 0 0"
          onClick={() => {
            navigate(`/events/${sluggedEvent?.slug}/bp-packages`);
          }}
        />
        <Button
          title={t('book-now')}
          backgroundColor={sluggedEvent?.design?.themeColor}
          hoverBackgroundColor={theme.color.white}
          textColor={theme.color.white}
          hoverTextColor={sluggedEvent?.design?.themeColor}
          borderRadius={theme.borderRadius.radius10}
          fontSize={
            smallerThanExtraLargeDesktop
              ? theme.fontSize.size15
              : theme.fontSize.size20
          }
          lineHeight={theme.lineHeight.height150}
          fontWeight="normal"
          padding="0.6rem 3rem"
          border="1px solid transparent"
          width="auto"
          onClick={() => {
            navigate(`/events/${sluggedEvent?.slug}${t('book-now-url')}`);
          }}
        />
      </ButtonsWrapper>
    </DesktopMenuWrapper>
  );
};
